import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { MonthlyFlowPoint } from '../../../client/IvtHubClient';
import { formatArr } from './scale';

/**
 * One row's three-month mini bar chart — the 新Open 趋势 and 上线趋势 columns.
 *
 * Hand-drawn divs, not a chart library: this package has no charting dependency and its
 * existing charts are hand-drawn SVG/canvas. Three fixed-width bars on a shared baseline
 * need no axes and no legend, so a library would be all cost.
 *
 * ⭐ ONE scale across the whole table and BOTH columns, like the vitality bars. A per-row or
 * per-column scale would make every row's tallest month full height, so "which month was
 * busy" would survive but "which industry is busy" would be erased — and comparing rows is
 * what a leader opens this page for. The basis is therefore the largest single month found
 * anywhere in the table (see `maxValue`), not a row's own maximum.
 *
 * ⚠️ The Figma bars are illustrative: 42 bars use only 7 distinct heights and the same
 * triples repeat across rows, so the heights are copy-pasted rather than derived. Geometry
 * (20px wide, 20px apart, bottom-aligned, 35px tallest) is measured; the mapping from count
 * to height is ours.
 *
 * ## The hover readout, and why it ended up like this
 *
 * Not in the design — requested separately, because a 20px bar with no printed number cannot
 * be read otherwise. Three approaches, in the order they were tried:
 *
 *  1. A CSS `:hover` panel inside the cell. **Clipped**: Cloudscape's table cell sets
 *     `overflow: hidden`, so only the panel's bottom edge showed at the row boundary.
 *  2. The native `title` attribute. Not clippable, but the browser owns both the delay
 *     (measured at two to three seconds) and the OS styling, and neither can be changed.
 *  3. This: a portal into `document.body`, positioned `fixed` from the bar's own
 *     `getBoundingClientRect()`. Instant, styled with the page, and out of reach of any
 *     ancestor's overflow.
 *
 * Anchored to the bar's column rather than to the cursor, so it does not jitter as the
 * pointer moves. It closes on scroll, because a `fixed` panel would otherwise drift away from
 * the bar it belongs to.
 *
 * The hit area is the whole COLUMN — bar plus month label — not just the bar. A bar for the
 * smallest non-zero month is only MIN_BAR_PX (3px) tall and a zero month is 0px, so bar-only
 * hovering made the smallest numbers the hardest to read and "0 opps" impossible to read.
 */

/** Tallest bar in the design; the value the shared scale maps its basis to. */
export const TREND_TRACK_PX = 35;

/**
 * Height the hover panel needs above a column, including its 8px offset.
 *
 * ⚠️ Hard-coded rather than measured, and that is a real limitation: change the panel's
 * padding, font sizes or the header rule in DashboardPage.css and this goes stale, at which
 * point the flip fires slightly early or slightly late. Measuring would need a layout pass
 * (the panel does not exist until it is hovered) and jsdom has none, so it could not be
 * tested. Erring high is the safe direction — it flips one row sooner than strictly needed,
 * which costs nothing.
 * 8 + (11 + 6 + 1 + 6) + 18 + 2 + 16 + 8 = 76, plus the 8px gap = 84, rounded up.
 */
const TIP_HEIGHT_PX = 88;

/**
 * `2026-07-01` → `7月`.
 *
 * Parsed with a regex rather than `new Date(...)`: the contract sends a UTC calendar date and
 * this only needs the month digits, so going through Date would reintroduce exactly the
 * local-vs-UTC question that windowPresets had to be rewritten to avoid.
 */
export function monthLabel(month: string | undefined): string {
  const m = /^\d{4}-(\d{2})/.exec(month ?? '');
  return m ? `${Number(m[1])}月` : '—';
}

/** Names the hovered series, so the panel says WHICH of the two columns it came from. */
const BUCKET_LABEL = { momentum: '新 Open', launched: '上线' } as const;

interface Hovered {
  month: string;
  count: number;
  arr: number | undefined;
  /** Viewport coordinates of the bar: the panel sits above its horizontal middle. */
  left: number;
  top: number;
  /** True when the column sits too close to the top of the window to draw above it. */
  below: boolean;
}

interface Props {
  /** Exactly three points, ascending — the contract guarantees it (`@length(min: 3, max: 3)`). */
  monthly: MonthlyFlowPoint[] | undefined;
  /** Which bucket this column draws. */
  bucket: 'momentum' | 'launched';
  /** Shared table-wide scale over single-month counts; see `linearScale`. */
  px: (value: number) => number;
}

export function TrendBars({ monthly, bucket, px }: Props) {
  const points = monthly ?? [];
  const [hovered, setHovered] = useState<Hovered | null>(null);

  const close = useCallback(() => setHovered(null), []);

  // A `fixed` panel holds viewport coordinates, so it would slide away from its bar as the
  // page scrolls. Closing is the honest fix; re-anchoring every scroll frame is a lot of work
  // for a panel the pointer has already left in practice.
  useEffect(() => {
    if (!hovered) return;
    window.addEventListener('scroll', close, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', close, { capture: true });
  }, [hovered, close]);

  return (
    <div className="ivt-dashboard__trend">
      {points.map((p, i) => {
        const count = p[bucket]?.oppCount ?? 0;
        return (
          <div
            className="ivt-dashboard__trend-col"
            key={p.month ?? i}
            // The handler sits on the COLUMN, not the bar, so the month label under it is part
            // of the hit area — a 20px x 4px bar is a hard target, and a zero-height bar was no
            // target at all (you could not read "0 opps" for an empty month).
            //
            // The anchor is the column's rect. Its horizontal centre is exactly the bar's — NOT
            // because they are the same width (the column is 28px so a two-digit month fits, the
            // bar is 20px) but because the track centres the bar in it; that `justify-content`
            // is what this arithmetic rests on. Its TOP is the track's top, not the bar's —
            // bars are bottom-aligned, so a 4px bar's own top sits 31px lower. That is on
            // purpose: the panel then appears at the same height for every month in a row
            // instead of jumping with the bar, and it can never cover the bar it describes.
            onMouseEnter={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setHovered({
                month: monthLabel(p.month),
                count,
                arr: p[bucket]?.arr,
                left: r.left + r.width / 2,
                top: r.top,
                // ⭐ Flip below when there is not enough headroom above. Without this the panel
                // is drawn off the top of the WINDOW for a row near the top of the scroller:
                // `position: fixed` in a body portal escapes every ancestor's overflow, but the
                // viewport edge is still a hard clip, and because the panel closes on scroll and
                // takes no pointer events, the lost part is unrecoverable. That is the same
                // information loss the portal was introduced to fix, one boundary further out.
                below: r.top < TIP_HEIGHT_PX,
              });
            }}
            onMouseLeave={close}
          >
            <div className="ivt-dashboard__trend-track">
              <div
                className={`ivt-dashboard__trend-bar is-${bucket}`}
                style={{ height: px(count) }}
                // Carries the same two numbers as the hover panel. Deliberately not focusable:
                // six bars per row would add ~60 tab stops to this table, so keyboard and
                // screen-reader users read the numbers here rather than chasing a tooltip.
                role="img"
                aria-label={`${monthLabel(p.month)} ${count} opps ${formatArr(p[bucket]?.arr)}`}
              />
            </div>
            <div className="ivt-dashboard__trend-month">{monthLabel(p.month)}</div>
          </div>
        );
      })}

      {hovered &&
        createPortal(
          // Into document.body on purpose: inside the cell, `overflow: hidden` clipped it.
          // `role="presentation"` because the same facts already reach assistive tech through
          // each bar's aria-label — announcing them twice is worse than not announcing here.
          <div
            className={`ivt-dashboard__trend-tip${hovered.below ? ' is-below' : ''}`}
            style={{ left: hovered.left, top: hovered.below ? hovered.top + TREND_TRACK_PX : hovered.top }}
            // `aria-hidden`, not `role="presentation"`: presentation only drops the element's
            // own semantics, it does NOT remove its text from the accessibility tree, so the
            // month and count would be announced twice — once here and once from the bar's
            // aria-label.
            aria-hidden="true"
          >
            <div className="ivt-dashboard__trend-tip-head">
              <i className={`is-${bucket}`} />
              {BUCKET_LABEL[bucket]} · {hovered.month}
            </div>
            <div className="ivt-dashboard__trend-tip-count">{hovered.count} opps</div>
            <div className="ivt-dashboard__trend-tip-arr">{formatArr(hovered.arr)}</div>
          </div>,
          document.body,
        )}
    </div>
  );
}
